import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { profile } from "@/data/portfolio";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

const Hero = () => {
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 50, damping: 18 });
    const sy = useSpring(my, { stiffness: 50, damping: 18 });
    const glowX = useTransform(sx, [-0.5, 0.5], [-48, 48]);
    const glowY = useTransform(sy, [-0.5, 0.5], [-32, 32]);
    const rotateX = useTransform(sy, [-0.5, 0.5], [3.5, -3.5]);

    const { scrollY } = useScroll();
    const gridY = useTransform(scrollY, [0, 900], [0, 140]);
    const fade = useTransform(scrollY, [0, 520], [1, 0]);

    const onMouseMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            id="hero"
            data-testid="hero-section"
            onMouseMove={onMouseMove}
            className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-4"
        >
            <motion.div style={{ y: gridY }} className="absolute inset-0" aria-hidden="true">
                <div className="bg-grid mask-hero absolute inset-0" />
            </motion.div>

            <div
                className="absolute left-1/2 top-[38%] -translate-x-1/2 -translate-y-1/2"
                aria-hidden="true"
            >
                <motion.div
                    style={{ x: glowX, y: glowY }}
                    className="animate-glow-drift h-[520px] w-[520px] rounded-full bg-flame/15 blur-[140px] sm:h-[640px] sm:w-[640px]"
                />
            </div>
            <div
                className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0b0c0f_80%)]"
                aria-hidden="true"
            />

            <motion.div
                style={{ opacity: fade, rotateX, transformPerspective: 1100 }}
                className="relative z-10 flex max-w-5xl flex-col items-center text-center"
            >
                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
                    data-testid="hero-eyebrow"
                    className="mb-8 flex items-center gap-2.5 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium tracking-wide text-slate-300 backdrop-blur-md sm:text-sm"
                >
                    <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-flame opacity-70" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-flame" />
                    </span>
                    Hello, I'm {profile.name}
                </motion.p>

                <h1 className="font-display text-[2.6rem] font-bold leading-[1.06] tracking-tight text-slate-50 sm:text-6xl lg:text-7xl xl:text-[5.25rem]">
                    <span className="block overflow-hidden pb-1">
                        <motion.span
                            className="block"
                            initial={{ y: "112%" }}
                            animate={{ y: 0 }}
                            transition={{ duration: 1.05, ease: EASE, delay: 0.25 }}
                        >
                            Crafting{" "}
                            <em className="font-serifit text-[1.05em] font-medium italic text-[#f6e3d3]">
                                Engaging,
                            </em>
                        </motion.span>
                    </span>
                    <span className="block overflow-hidden pb-2">
                        <motion.span
                            className="block"
                            initial={{ y: "112%" }}
                            animate={{ y: 0 }}
                            transition={{ duration: 1.05, ease: EASE, delay: 0.39 }}
                        >
                            Impactful{" "}
                            <em className="font-serifit text-[1.05em] font-medium italic text-[#f6e3d3]">
                                Visual Stories.
                            </em>
                        </motion.span>
                    </span>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
                    data-testid="hero-subline"
                    className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
                >
                    {profile.tagline}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: EASE, delay: 0.9 }}
                    className="mt-10"
                >
                    <button
                        data-testid="hero-view-work-btn"
                        onClick={() => scrollToId("work")}
                        className="group inline-flex items-center gap-2 rounded-full bg-flame px-7 py-3.5 text-sm font-semibold text-white shadow-[0_0_44px_rgba(255,75,38,0.35)] transition-[background-color,box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:bg-flame-hover hover:shadow-[0_0_64px_rgba(255,75,38,0.5)]"
                    >
                        View My Work
                        <ArrowUpRight
                            size={16}
                            className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        />
                    </button>
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
                aria-hidden="true"
            >
                <div className="flex flex-col items-center gap-3">
                    <span className="font-mono text-[10px] uppercase tracking-[0.35em] text-slate-500">
                        Scroll
                    </span>
                    <span className="block h-10 w-px overflow-hidden bg-white/10">
                        <motion.span
                            className="block h-4 w-px bg-flame"
                            animate={{ y: [0, 40] }}
                            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                        />
                    </span>
                </div>
            </motion.div>
        </section>
    );
};

export default Hero;
